import React, { useState, useEffect, useCallback } from 'react';
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    CloseIcon,
    ImageIcon,
    EyeIcon
} from './Icons';

/**
 * MD3BlogCarousel - Material Design 3 Image Carousel & Gallery
 * Supports single images, multiple images, touch gestures, keyboard navigation,
 * thumbnail preview bar, and a fullscreen lightbox view.
 */
const MD3BlogCarousel = ({
    images = [],
    imageUrl = null,
    title = '',
    getAssetUrl = (url) => url,
    className = ''
}) => {
    // Normalize images list
    const normalizedImages = React.useMemo(() => {
        let list = [];
        if (Array.isArray(images) && images.length > 0) {
            list = images.filter(Boolean);
        } else if (imageUrl) {
            list = [imageUrl];
        }
        return list.map(img => typeof img === 'string' ? img : (img?.url || ''));
    }, [images, imageUrl]);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [touchStartX, setTouchStartX] = useState(null);
    const [touchEndX, setTouchEndX] = useState(null);

    const totalImages = normalizedImages.length;

    // Reset index if image list changes
    useEffect(() => {
        if (currentIndex >= totalImages && totalImages > 0) {
            setCurrentIndex(0);
        }
    }, [totalImages, currentIndex]);

    const nextSlide = useCallback(() => {
        if (totalImages <= 1) return;
        setCurrentIndex(prev => (prev + 1) % totalImages);
    }, [totalImages]);

    const prevSlide = useCallback(() => {
        if (totalImages <= 1) return;
        setCurrentIndex(prev => (prev - 1 + totalImages) % totalImages);
    }, [totalImages]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (lightboxOpen) {
                if (e.key === 'Escape') setLightboxOpen(false);
                if (e.key === 'ArrowRight') nextSlide();
                if (e.key === 'ArrowLeft') prevSlide();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [lightboxOpen, nextSlide, prevSlide]);

    // Touch Swipe Handlers
    const handleTouchStart = (e) => {
        setTouchStartX(e.targetTouches[0].clientX);
        setTouchEndX(null);
    };

    const handleTouchMove = (e) => {
        setTouchEndX(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStartX || !touchEndX) return;
        const diff = touchStartX - touchEndX;
        const minSwipeDistance = 45;
        if (diff > minSwipeDistance) {
            nextSlide();
        } else if (diff < -minSwipeDistance) {
            prevSlide();
        }
        setTouchStartX(null);
        setTouchEndX(null);
    };

    if (totalImages === 0) {
        return null;
    }

    const currentImgUrl = getAssetUrl(normalizedImages[currentIndex]);

    return (
        <div className={`w-full my-6 select-none ${className}`}>
            {/* Main Carousel Frame (Framed & Embedded) */}
            <div className="relative group rounded-[2rem] p-2 md:p-3 bg-surface-containerHigh/60 border border-outlineVariant/50 shadow-elevation-2">
                {/* Visual Viewport with Framed Image */}
                <div
                    className="relative w-full aspect-[4/3] sm:aspect-[16/10] max-h-[480px] rounded-[1.4rem] overflow-hidden bg-surface-containerLow flex items-center justify-center cursor-pointer"
                    onClick={() => setLightboxOpen(true)}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    role="region"
                    aria-label={`Bildergalerie für ${title || 'Blogbeitrag'}`}
                >
                    {/* Ambient subtle background */}
                    <div
                        className="absolute inset-0 bg-cover bg-center blur-xl opacity-20 scale-105 pointer-events-none transition-all duration-medium3"
                        style={{ backgroundImage: `url(${currentImgUrl})` }}
                    />
                    <div className="absolute inset-0 bg-surface/10 pointer-events-none" />

                    {/* Sharp Main Image */}
                    <img
                        key={currentIndex}
                        src={currentImgUrl}
                        alt={`${title || 'Blogbild'} - Foto ${currentIndex + 1}`}
                        className="relative z-10 w-full h-full object-contain sm:object-cover md:object-contain rounded-[1.4rem] transition-all duration-medium3 animate-fade-in"
                        loading="lazy"
                    />

                    {/* Bottom subtle gradient overlay for controls legibility */}
                    {totalImages > 1 && (
                        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/50 via-black/15 to-transparent z-10 pointer-events-none rounded-b-[1.4rem]" />
                    )}

                    {/* Fullscreen / Zoom Button */}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setLightboxOpen(true);
                        }}
                        className="absolute top-4 right-4 z-20 flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-surface/80 hover:bg-surface text-surface-on backdrop-blur-md shadow-elevation-1 transition-all hover:scale-105 active:scale-95 text-label-small font-bold"
                        title="Vollbildansicht öffnen"
                        aria-label="Vollbildansicht"
                    >
                        <EyeIcon sx={{ fontSize: 16 }} />
                        <span className="hidden sm:inline">Vergrößern</span>
                    </button>

                    {/* Image Counter Badge */}
                    {totalImages > 1 && (
                        <div className="absolute top-4 left-4 z-20 flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-black/60 backdrop-blur-md text-white shadow-elevation-1 text-label-small font-semibold">
                            <ImageIcon sx={{ fontSize: 16 }} />
                            <span>{currentIndex + 1} / {totalImages}</span>
                        </div>
                    )}
                </div>

                {/* Floating Navigation Buttons (Previous / Next) */}
                {totalImages > 1 && (
                    <>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                prevSlide();
                            }}
                            className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-surface/85 hover:bg-surface text-surface-on backdrop-blur-md shadow-elevation-2 flex items-center justify-center transition-all hover:scale-110 active:scale-95 border border-outlineVariant/30 opacity-90 group-hover:opacity-100 focus:opacity-100"
                            title="Vorheriges Bild"
                            aria-label="Vorheriges Bild"
                        >
                            <ChevronLeftIcon sx={{ fontSize: 24 }} />
                        </button>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                nextSlide();
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-surface/85 hover:bg-surface text-surface-on backdrop-blur-md shadow-elevation-2 flex items-center justify-center transition-all hover:scale-110 active:scale-95 border border-outlineVariant/30 opacity-90 group-hover:opacity-100 focus:opacity-100"
                            title="Nächstes Bild"
                            aria-label="Nächstes Bild"
                        >
                            <ChevronRightIcon sx={{ fontSize: 24 }} />
                        </button>

                        {/* MD3 Indicator Pills */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-md border border-white/10">
                            {normalizedImages.map((_, idx) => (
                                <button
                                    key={idx}
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCurrentIndex(idx);
                                    }}
                                    className={`transition-all duration-medium2 rounded-full ${
                                        idx === currentIndex
                                            ? 'w-7 h-2 bg-primary shadow-sm'
                                            : 'w-2 h-2 bg-white/60 hover:bg-white/90'
                                    }`}
                                    title={`Zu Bild ${idx + 1} springen`}
                                    aria-label={`Zu Bild ${idx + 1} springen`}
                                    aria-current={idx === currentIndex ? 'true' : 'false'}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* Thumbnail Strip (only when more than 1 image) */}
            {totalImages > 1 && (
                <div className="flex items-center gap-3 mt-4 overflow-x-auto pb-2 scrollbar-none px-1">
                    {normalizedImages.map((img, idx) => {
                        const thumbUrl = getAssetUrl(img);
                        const isActive = idx === currentIndex;
                        return (
                            <button
                                key={idx}
                                type="button"
                                onClick={() => setCurrentIndex(idx)}
                                className={`relative flex-shrink-0 w-20 h-14 md:w-24 md:h-16 rounded-2xl overflow-hidden transition-all duration-medium1 border-2 ${
                                    isActive
                                        ? 'border-primary ring-2 ring-primary/30 scale-105 shadow-elevation-2 opacity-100'
                                        : 'border-transparent opacity-60 hover:opacity-100 hover:scale-102'
                                }`}
                                aria-label={`Vorschau Bild ${idx + 1}`}
                            >
                                <img
                                    src={thumbUrl}
                                    alt={`Miniaturansicht ${idx + 1}`}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Fullscreen Lightbox Modal */}
            {lightboxOpen && (
                <div
                    className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-xl flex flex-col justify-between p-4 md:p-8 animate-fade-in"
                    onClick={() => setLightboxOpen(false)}
                >
                    {/* Top Bar */}
                    <div className="flex items-center justify-between z-10" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-3 text-white">
                            <span className="text-title-medium font-bold line-clamp-1">{title || 'Bildanzeige'}</span>
                            {totalImages > 1 && (
                                <span className="text-label-medium px-3 py-1 rounded-full bg-white/10 text-white/90">
                                    {currentIndex + 1} / {totalImages}
                                </span>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={() => setLightboxOpen(false)}
                            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors shadow-elevation-1"
                            title="Schließen (Esc)"
                            aria-label="Schließen"
                        >
                            <CloseIcon sx={{ fontSize: 22 }} />
                        </button>
                    </div>

                    {/* Lightbox Center Image */}
                    <div
                        className="relative flex-1 flex items-center justify-center my-4 overflow-hidden"
                        onClick={e => e.stopPropagation()}
                    >
                        <img
                            key={currentIndex}
                            src={currentImgUrl}
                            alt={`${title || 'Blogbild'} - Großansicht ${currentIndex + 1}`}
                            className="max-h-[82vh] max-w-full object-contain rounded-2xl shadow-elevation-4 animate-fade-in"
                        />

                        {totalImages > 1 && (
                            <>
                                <button
                                    type="button"
                                    onClick={prevSlide}
                                    className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 hover:bg-white/30 text-white backdrop-blur-md flex items-center justify-center transition-all hover:scale-110 shadow-elevation-2"
                                    title="Vorheriges Bild"
                                    aria-label="Vorheriges Bild"
                                >
                                    <ChevronLeftIcon sx={{ fontSize: 30 }} />
                                </button>
                                <button
                                    type="button"
                                    onClick={nextSlide}
                                    className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 hover:bg-white/30 text-white backdrop-blur-md flex items-center justify-center transition-all hover:scale-110 shadow-elevation-2"
                                    title="Nächstes Bild"
                                    aria-label="Nächstes Bild"
                                >
                                    <ChevronRightIcon sx={{ fontSize: 30 }} />
                                </button>
                            </>
                        )}
                    </div>

                    {/* Bottom Lightbox Thumbnails */}
                    {totalImages > 1 && (
                        <div
                            className="flex items-center justify-center gap-2 overflow-x-auto py-2 z-10"
                            onClick={e => e.stopPropagation()}
                        >
                            {normalizedImages.map((img, idx) => {
                                const thumbUrl = getAssetUrl(img);
                                const isActive = idx === currentIndex;
                                return (
                                    <button
                                        key={idx}
                                        type="button"
                                        onClick={() => setCurrentIndex(idx)}
                                        className={`w-16 h-12 rounded-xl overflow-hidden transition-all duration-medium1 border-2 ${
                                            isActive
                                                ? 'border-primary scale-110 ring-2 ring-primary/40 opacity-100'
                                                : 'border-transparent opacity-50 hover:opacity-90'
                                        }`}
                                    >
                                        <img
                                            src={thumbUrl}
                                            alt={`Vorschau ${idx + 1}`}
                                            className="w-full h-full object-cover"
                                        />
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MD3BlogCarousel;
